import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { usePostHog } from "posthog-js/react";
import { authClient } from "../lib/auth";
import { buildAuthCallbackUrl, type AuthFlowOptions } from "../lib/invitation-flow";
import { localUsernameToEmail } from "../lib/local-users";
import type { AuthSession } from "../types";

type SignupResult =
  | { status: "signed-in" }
  | { status: "verification-required"; email: string; emailSent: boolean }
  | { status: "signin-required"; email: string };

type AuthContextValue = {
  user: AuthSession | null;
  loading: boolean;
  refresh: () => Promise<void>;
  login: (email: string, password: string, options?: AuthFlowOptions) => Promise<void>;
  signup: (input: {
    name: string;
    email: string;
    password: string;
    organizationName?: string;
    invitationId?: string;
    redirectPath?: string;
    invitedEmail?: string;
    invitedOrganizationName?: string;
    invitedOrganizationId?: string;
  }) => Promise<SignupResult>;
  logout: () => Promise<void>;
  switchOrganization: (organizationId: string) => Promise<void>;
  createOrganization: (name: string) => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

const slugify = (value: string): string =>
  value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 48);

const PENDING_ORGANIZATION_STORAGE_KEY = "pending-organization-signup";

type PendingOrganizationSignup = {
  email: string;
  organizationName: string;
};

const readPendingOrganizationSignup = (): PendingOrganizationSignup | null => {
  const raw = window.localStorage.getItem(PENDING_ORGANIZATION_STORAGE_KEY);
  if (!raw) {
    const searchParams = new URLSearchParams(window.location.search);
    if (searchParams.get("signup") !== "1") {
      return null;
    }

    const email = searchParams.get("signupEmail")?.trim().toLowerCase() ?? "";
    const organizationName = searchParams.get("pendingOrg")?.trim() ?? "";

    if (!email || !organizationName) {
      return null;
    }

    return {
      email,
      organizationName,
    };
  }

  try {
    const parsed = JSON.parse(raw) as Partial<PendingOrganizationSignup>;
    if (
      typeof parsed.email === "string" &&
      typeof parsed.organizationName === "string" &&
      parsed.email &&
      parsed.organizationName
    ) {
      return {
        email: parsed.email,
        organizationName: parsed.organizationName,
      };
    }
  } catch {
    // Ignore malformed local storage values.
  }

  window.localStorage.removeItem(PENDING_ORGANIZATION_STORAGE_KEY);
  return null;
};

const storePendingOrganizationSignup = (value: PendingOrganizationSignup): void => {
  window.localStorage.setItem(PENDING_ORGANIZATION_STORAGE_KEY, JSON.stringify(value));
};

const clearPendingOrganizationSignup = (): void => {
  window.localStorage.removeItem(PENDING_ORGANIZATION_STORAGE_KEY);
};

const buildAuthSession = async (): Promise<AuthSession | null> => {
  const sessionResult = await authClient.getSession();
  const session = sessionResult.data;

  if (!session?.user || !session.session) {
    return null;
  }

  const organizationsResult = await authClient.organization.list();
  const organizations = organizationsResult.data ?? [];

  let activeOrganizationId = session.session.activeOrganizationId ?? null;

  if (!activeOrganizationId && organizations.length > 0) {
    await authClient.organization.setActive({
      organizationId: organizations[0].id,
    });
    const refreshedSessionResult = await authClient.getSession();
    activeOrganizationId = refreshedSessionResult.data?.session.activeOrganizationId ?? organizations[0].id;
  }

  const activeOrganization =
    organizations.find((organization) => organization.id === activeOrganizationId) ?? null;

  return {
    id: session.user.id,
    name: session.user.name,
    email: session.user.email,
    activeOrganizationId,
    activeOrganization,
    organizations,
  };
};

const finalizePendingOrganization = async (
  session: AuthSession | null,
): Promise<AuthSession | null> => {
  if (!session) {
    return null;
  }

  const pendingSignup = readPendingOrganizationSignup();
  if (!pendingSignup) {
    return session;
  }

  if (pendingSignup.email !== session.email.toLowerCase()) {
    return session;
  }

  if (session.organizations.length > 0) {
    clearPendingOrganizationSignup();
    return session;
  }

  try {
    const organizationResult = await authClient.organization.create({
      name: pendingSignup.organizationName,
      slug: slugify(pendingSignup.organizationName) || "workspace",
    });

    if (organizationResult.error || !organizationResult.data) {
      throw new Error(
        organizationResult.error?.message || "Unable to create organization",
      );
    }

    await authClient.organization.setActive({
      organizationId: organizationResult.data.id,
    });

    clearPendingOrganizationSignup();
    return buildAuthSession();
  } catch (error) {
    clearPendingOrganizationSignup();
    console.error("Unable to finalize organization after verification", error);
    return session;
  }
};

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthSession | null>(null);
  const [loading, setLoading] = useState(true);
  const posthog = usePostHog();
  const identifiedUserIdRef = useRef<string | null>(null);

  const refresh = useCallback(async () => {
    try {
      const nextUser = await finalizePendingOrganization(await buildAuthSession());
      setUser(nextUser);
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  useEffect(() => {
    if (!user) {
      if (identifiedUserIdRef.current) {
        posthog.reset();
        identifiedUserIdRef.current = null;
      }
      return;
    }

    posthog.identify(user.id, {
      email: user.email,
      name: user.name,
      activeOrganizationId: user.activeOrganizationId,
      activeOrganizationName: user.activeOrganization?.name ?? null,
      organizationCount: user.organizations.length,
    });
    identifiedUserIdRef.current = user.id;
  }, [posthog, user]);

  const login = useCallback(async (email: string, password: string, options?: AuthFlowOptions) => {
    const result = await authClient.signIn.email({
      email: localUsernameToEmail(email),
      password,
      callbackURL: buildAuthCallbackUrl(window.location.origin, options),
    });

    if (result.error) {
      throw new Error(result.error.message || "Unable to log in");
    }

    const nextUser = await finalizePendingOrganization(await buildAuthSession());
    setUser(nextUser);
  }, []);

  const signup = useCallback(
    async ({
      name,
      email,
      password,
      organizationName,
      invitationId,
      redirectPath,
      invitedEmail,
      invitedOrganizationName,
      invitedOrganizationId,
    }: {
      name: string;
      email: string;
      password: string;
      organizationName?: string;
      invitationId?: string;
      redirectPath?: string;
      invitedEmail?: string;
      invitedOrganizationName?: string;
      invitedOrganizationId?: string;
    }) => {
      const normalizedEmail = email.trim().toLowerCase();
      const callbackOptions = {
        invitationId,
        invitedEmail,
        invitedOrganizationName,
        invitedOrganizationId,
        redirectPath,
        signupEmail: organizationName ? normalizedEmail : undefined,
        signupOrganizationName: organizationName || undefined,
      } satisfies AuthFlowOptions;
      const signupResult = await authClient.signUp.email({
        name,
        email: normalizedEmail,
        password,
        callbackURL: buildAuthCallbackUrl(window.location.origin, callbackOptions),
      });

      if (signupResult.error) {
        throw new Error(signupResult.error.message || "Unable to create account");
      }

      if (!signupResult.data?.token) {
        const signInResult = await authClient.signIn.email({
          email: normalizedEmail,
          password,
          callbackURL: buildAuthCallbackUrl(window.location.origin, callbackOptions),
        });

        if (!signInResult.error && signInResult.data) {
          clearPendingOrganizationSignup();
          const nextUser = await finalizePendingOrganization(await buildAuthSession());
          setUser(nextUser);
          return {
            status: "signed-in" as const,
          };
        }

        if (signInResult.error?.message === "Email not verified") {
          if (organizationName) {
            storePendingOrganizationSignup({
              email: normalizedEmail,
              organizationName,
            });
          } else {
            clearPendingOrganizationSignup();
          }

          setUser(null);
          return {
            status: "verification-required" as const,
            email: normalizedEmail,
            emailSent: true,
          };
        }

        clearPendingOrganizationSignup();
        setUser(null);
        return {
          status: "signin-required" as const,
          email: normalizedEmail,
        };
      }

      if (organizationName) {
        const organizationResult = await authClient.organization.create({
          name: organizationName,
          slug: slugify(organizationName) || "workspace",
        });

        if (organizationResult.error || !organizationResult.data) {
          throw new Error(
            organizationResult.error?.message || "Unable to create organization",
          );
        }

        await authClient.organization.setActive({
          organizationId: organizationResult.data.id,
        });
      }

      const nextUser = await finalizePendingOrganization(await buildAuthSession());
      setUser(nextUser);
      clearPendingOrganizationSignup();
      return {
        status: "signed-in" as const,
      };
    },
    [],
  );

  const logout = useCallback(async () => {
    await authClient.signOut();
    setUser(null);
  }, []);

  const switchOrganization = useCallback(async (organizationId: string) => {
    const result = await authClient.organization.setActive({ organizationId });
    if (result.error) {
      throw new Error(result.error.message || "Unable to switch organization");
    }

    const nextUser = await buildAuthSession();
    setUser(nextUser);
  }, []);

  const createOrganization = useCallback(async (name: string) => {
    const result = await authClient.organization.create({
      name,
      slug: slugify(name) || "workspace",
    });

    if (result.error || !result.data) {
      throw new Error(result.error?.message || "Unable to create organization");
    }

    await authClient.organization.setActive({
      organizationId: result.data.id,
    });

    const nextUser = await buildAuthSession();
    setUser(nextUser);
  }, []);

  const value = useMemo(
    () => ({
      user,
      loading,
      refresh,
      login,
      signup,
      logout,
      switchOrganization,
      createOrganization,
    }),
    [createOrganization, loading, login, logout, refresh, signup, switchOrganization, user],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used within AuthProvider");
  }
  return context;
}
